import { useState, useEffect, useMemo, useRef } from "react";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import { Box, CircularProgress, Icon, Fab, Snackbar, Alert } from "@mui/material";
import MDBox from "components/MDBox";
import { useAuth } from "context/authContext";
import { MarkerType } from "reactflow";
import { Typography } from "@mui/material";
import {
  getActiveKpisByJobId,
  addKpiLink,
  getKpiLinksWithNames,
  deleteKpiLinkById,
} from "services/api";
import ConfirmationDialog from "components/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import AddStrategicRoadmapDialog from "components/AddStrategicRoadmapDialog";
import dagre from "dagre";
import { CustomKpiNode, CustomEdgeWithDelete } from "services/utils";
import AddFloatingButton from "components/AddFloatingButton";
import SnackbarWrapper from "components/Snackbar";
import PropTypes from "prop-types";

const buildGraphLayout = (kpis, links, onEdgeDelete, t) => {
  const g = new dagre.graphlib.Graph(); //Initializes a Dagre graph object. used to define nodes and edges before running the layout.
  g.setGraph({ rankdir: "BT" });
  g.setDefaultEdgeLabel(() => ({}));

  const nodeWidth = 180;
  const nodeHeight = 60;
  const verticalSpacing = 200;

  //put kpis as nodes
  kpis?.forEach(([id, kpiNameId]) => {
    g.setNode(String(kpiNameId), { width: nodeWidth, height: nodeHeight });
  });

  // put links between nodes
  links?.forEach(([sourceId, , targetId]) => {
    g.setEdge(String(sourceId), String(targetId));
  });

  dagre.layout(g);

  //Define perspective categories and Y positions
  const perspectives = [
    "Finance Viability",
    "External StakeHolder",
    "Internal Process",
    "Internal StakeHolder",
  ];
  const perspectiveYPositions = perspectives.reduce((acc, name, index) => {
    acc[name] = index * verticalSpacing;
    return acc;
  }, {});

  // Count incoming links (arrows) to each KPI
  const incomingCountMap = {};

  links?.forEach(([, , targetId]) => {
    incomingCountMap[targetId] = (incomingCountMap[targetId] || 0) + 1;
  });

  const perspectiveNodeCounts = {};
  // Build the kpi nodes
  const rawNodes = kpis
    ?.filter(
      (kpi) =>
        !kpi[3] ||
        [
          "Finance Viability",
          "Internal Process",
          "Internal StakeHolder",
          "External StakeHolder",
        ].includes(kpi[3])
    )
    .map(([id, kpiNameId, kpiName, perspectiveName]) => {
      const y = perspectiveYPositions[perspectiveName] || 0; // determines the y-position of the node

      if (!perspectiveNodeCounts[perspectiveName]) perspectiveNodeCounts[perspectiveName] = 0;
      const x = perspectiveNodeCounts[perspectiveName] * (nodeWidth + 100); //horizontal position of node
      perspectiveNodeCounts[perspectiveName]++;

      return {
        id: String(kpiNameId),
        type: "kpiNode",
        data: {
          label: kpiName,
          incomingCount: incomingCountMap[kpiNameId] || 0,
        },
        position: { x, y },
        sourcePosition: "top",
        targetPosition: "bottom",
      };
    });

  // Utility: generate HSL color from index
  const generateHSLColor = (index) => {
    const hue = (index * 137.508) % 360; // use golden angle for even distribution
    return `hsl(${hue}, 70%, 60%)`;
  };

  // Build source-to-color map
  const sourceColorMap = {};

  // Loop through all KPI relationships,generates the edges (connections) between KPI nodes
  const targetIncomingMap = {};

  const rawEdges = links.map(([sourceId, , targetId, , linkId], index) => {
    const handleIndex = targetIncomingMap[targetId] ?? 0;
    const handleId = `bottom-${handleIndex}`;

    targetIncomingMap[targetId] = handleIndex + 1;

    if (!sourceColorMap[sourceId]) {
      sourceColorMap[sourceId] = generateHSLColor(Object.keys(sourceColorMap).length);
    }

    return {
      id: `edge-${sourceId}-${targetId}`,
      source: String(sourceId),
      target: String(targetId),
      sourceHandle: "top-source",
      targetHandle: "bottom-target",
      type: "customEdge",
      data: {
        linkId,
        onDelete: () =>
          onEdgeDelete({
            id: `edge-${sourceId}-${targetId}-${handleIndex}`,
            data: { linkId },
          }),
      },
      style: {
        stroke: sourceColorMap[sourceId],
        strokeWidth: 3,
        strokeDasharray: "6 4",
        zIndex: 1200,
      },
      animated: true,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: sourceColorMap[sourceId],
        width: 11,
        height: 11,
      },
    };
  });

  const labelNodes = perspectives.map((perspective, index) => {
    const y = index * 200; // match your row Y spacing

    return {
      id: `label-${perspective}`,
      type: "input", // or custom type
      data: {
        // This matches your MenuItem logic exactly
        label: t(`user.assess.perspectives.${perspective}`),
      },
      // data: { label: perspective },
      position: { x: -300, y }, // place to the left of the row
      draggable: false,
      selectable: false,
      style: {
        width: 220, // Slightly wider to accommodate text and padding
        height: 40, // Taller for more presence
        background: "#ffffff", // Clean white background
        color: "#333", // Dark text
        border: "1px solid #A7DBD8", // Light teal border, consistent with KPI nodes
        borderLeft: "8px solid #009688", // Strong, prominent teal bar on the left
        borderRadius: "8px", // Match the rounded corners of your KPI nodes
        boxShadow: "2px 4px 8px rgba(0, 0, 0, 0.1)", // More pronounced shadow for elevation
        fontWeight: "600", // Bolder font
        fontSize: "15px", // Larger font size
        textAlign: "left", // Align text to the left after the teal bar
        paddingLeft: "15px", // Padding after the thick border
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start", // Align content to the start (left)
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // Optional: Add a subtle text shadow for depth (be cautious with this)
        // textShadow: '0 1px 1px rgba(0,0,0,0.05)'
      },
    };
  });

  return {
    nodes: [...labelNodes, ...rawNodes],
    edges: rawEdges,
  };
};

function StrategicRoadmapView({ canEdit }) {
  const [openStrategicDialog, setOpenStrategicDialog] = useState(false);
  const [activeKpis, setActiveKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpiLinks, setKpiLinks] = useState([]); // kpi relations
  const { employee } = useAuth();
  const [confOpen, setConfOpen] = useState(false);
  const [edgeToDelete, setEdgeToDelete] = useState(null);
  const { t } = useTranslation();

  const snackbarRef = useRef();

  const handleOpenStrategicDialog = () => {
    setOpenStrategicDialog(true); //opening the dialog for the user to see
  };

  const handleCloseStrategicDialog = () => {
    setOpenStrategicDialog(false);
  };

  const handleAddStrategic = async (data) => {
    if (!canEdit) return;
    try {
      await addKpiLink(data);
      const freshLinks = await getKpiLinksWithNames();
      if (freshLinks != "error") setKpiLinks(freshLinks);
      setOpenStrategicDialog(false);
      snackbarRef.current?.showSnackbar(t("user.kpiLinks.kpiLinkAdded"), "success");
    } catch (error) {
      snackbarRef.current?.showSnackbar(error.message || "An error occurred", "error");
    }
  };

  const handleEdgeDelete = async (edge) => {
    if (!canEdit) return;
    const { linkId } = edge.data || {};
    if (!linkId) return;

    const result = await deleteKpiLinkById(linkId);
    if (result !== "error") {
      const freshLinks = await getKpiLinksWithNames();
      if (freshLinks !== "error") setKpiLinks(freshLinks);
    } else {
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (employee?.role === 1) {
        const kpiLinks = await getKpiLinksWithNames();
        if (kpiLinks != "error") setKpiLinks(kpiLinks); //kpiLinks has the source kpiNameId,source kpiName and target...
        const activeKpisData = await getActiveKpisByJobId(employee.jobId);
        if (activeKpisData != "error") setActiveKpis(activeKpisData);
        // active kpis has number of arrays, each has kpi assignemt id, kpi Name Id, kpi Name
      } else {
      }
    };
    fetchData().then((e) => {
      setLoading(false);
    });
  }, [employee]);

  const nodeTypes = {
    kpiNode: CustomKpiNode,
  };

  const edgeTypes = {
    customEdge: CustomEdgeWithDelete,
  };

  // avoid recomputation of the graph, re-run the buildGraphLayout function only if state changes
  const graphData = useMemo(() => {
    // return buildGraphLayout(activeKpis, kpiLinks);
    return buildGraphLayout(
      activeKpis,
      kpiLinks,
      (edge) => {
        setEdgeToDelete(edge);
        setConfOpen(true);
      },
      t
    );
  }, [activeKpis, kpiLinks]);

  const nodes = graphData.nodes;
  const edges = graphData.edges;

  return (
    <MDBox p={3}>
      {loading ? (
        <Box display="flex" justifyContent="center" p={5}>
          <CircularProgress size={20} />
        </Box>
      ) : nodes.length === 0 ? (
        <Typography
          variant="h6"
          textAlign="center"
          sx={{ height: "500px", display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          {"noData"} {/* Or any appropriate no data message */}
        </Typography>
      ) : (
        <MDBox height="500px" borderRadius="lg" sx={{ background: "#fff" }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
          >
            <Background />
            <Controls />
          </ReactFlow>
        </MDBox>
      )}

      <AddFloatingButton disabled={!canEdit} onClick={handleOpenStrategicDialog} />

      <AddStrategicRoadmapDialog
        open={openStrategicDialog}
        onClose={handleCloseStrategicDialog}
        handleSubmit={handleAddStrategic}
        activeKpis={activeKpis}
      />

      <ConfirmationDialog
        open={confOpen}
        onClose={() => {
          setConfOpen(false);
        }}
        onConfirm={async () => {
          if (edgeToDelete) {
            await handleEdgeDelete(edgeToDelete);
          }
          setConfOpen(false);
        }}
      />

      <SnackbarWrapper ref={snackbarRef} anchorOrigin={{ vertical: "top", horizontal: "center" }} />
    </MDBox>
  );
}

StrategicRoadmapView.propTypes = {
  canEdit: PropTypes.bool.isRequired,
};

export default StrategicRoadmapView;
